import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { FaEye, FaEdit, FaTrash, FaSearch, FaTimes, FaPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { setWaadFilters, resetWaadFilters } from '../../redux/features/filtersSlice';
import { useGetWaadQuery } from "../../redux/services/waadService/waad.Services";
import CommonBreadcrumb from '../../common/CommonBreadcrumb';
function Waad() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // ✅ Get waad filters from Redux
    const waadFilters = useSelector((state) => state.filters.waad);
    
    // Local state for UI
    const [statusFilter, setStatusFilter] = useState(waadFilters.status || '');
    const [searchTerm, setSearchTerm] = useState(waadFilters.search || '');

    // ✅ Use waadFilters for API call
    const { data, isLoading, isError, refetch } = useGetWaadQuery(waadFilters);

    const waadData = data?.data || [];
    const pagination = data?.pagination || {};
    const totalPages = pagination?.totalPages || 0;
    const totalItems = pagination?.total || 0;
    const currentPage = pagination?.page || 1;
    const showPagination = totalPages > 1;

    // ✅ Status check - 1 = Active, 0 = Inactive
    const isActive = (status) => {
        return status === 1 || status === "1" || status === true;
    };

    // ✅ Sync local state with Redux when filters change
    useEffect(() => {
        setStatusFilter(waadFilters.status || '');
        setSearchTerm(waadFilters.search || '');
    }, [waadFilters]);

    // Handle page change
    const handlePageChange = (page) => {
        dispatch(
            setWaadFilters({
                page: page,
            })
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle search
    const handleSearch = (value) => {
        setSearchTerm(value);
        dispatch(
            setWaadFilters({
                search: value,
                page: 1,
            })
        );
    };

    // Handle refresh
    const refetchData = () => {
        refetch();
        successToast("Data refreshed successfully!");
    };

    // Handle status filter
    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        dispatch(
            setWaadFilters({
                status: status,
                page: 1,
            })
        );
    };

    // ✅ Clear all filters using resetWaadFilters
    const clearFilters = () => {
        setStatusFilter('');
        setSearchTerm('');
        dispatch(resetWaadFilters());
    };

    // Handle limit change
    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        dispatch(
            setWaadFilters({
                limit: newLimit,
                page: 1,
            })
        );
    };

    // Handle refresh
    const handleRefresh = () => {
        refetch();
        successToast("Data refreshed successfully!");
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mt-3">Loading Waad data...</h5>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5">
                <div className="alert alert-danger">
                    <h5>Something Went Wrong</h5>
                    <p>Unable to load Waad data. Please try again.</p>
                    <button className="btn btn-primary" onClick={handleRefresh}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* HEADER */}
           <CommonBreadcrumb
    title="All Waad"
    breadcrumbs={[
        { label: "Home", link: "/" },
        { label: "All Waad" },
    ]}
    buttonText="Add Waad"
    buttonIcon={<FaPlus className="me-1" />}
    buttonLink="/add-waad"
/>

            {/* SEARCH AND FILTER SECTION */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-center">
                        {/* Search Bar */}
                        <div className="col-md-4">
                            <SearchBar
                                search={searchTerm}
                                setSearch={handleSearch}
                                placeholder="Search Waad by name or code..."
                            />
                        </div>

                        {/* ✅ Status Filter - 1 = Active, 0 = Inactive */}
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => handleStatusFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        {/* Limit Filter */}
                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={waadFilters.limit || 10}
                                onChange={handleLimitChange}
                            >
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-md-3 text-md-end">
                            {(waadFilters.search || waadFilters.status) && (
                                <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={clearFilters}
                                >
                                    <FaTimes className="me-1" />
                                    Clear Filters
                                </button>
                            )}
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleRefresh}
                            >
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(waadFilters.search || waadFilters.status) && (
                        <div className="mt-3 d-flex flex-wrap gap-2">
                            <span className="text-muted me-2">Active Filters:</span>
                            {waadFilters.search && (
                                <span className="badge bg-primary d-flex align-items-center gap-1">
                                    Search: {waadFilters.search}
                                    <button
                                        className="btn btn-link btn-sm text-white p-0 ms-1"
                                        onClick={() => handleSearch('')}
                                    >
                                        <FaTimes />
                                    </button>
                                </span>
                            )}
                            {waadFilters.status && (
                                <span className="badge bg-info d-flex align-items-center gap-1">
                                    Status: {waadFilters.status === '1' ? 'Active' : 'Inactive'}
                                    <button
                                        className="btn btn-link btn-sm text-white p-0 ms-1"
                                        onClick={() => handleStatusFilter('')}
                                    >
                                        <FaTimes />
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* TABLE */}
            <div className="st-wrapper-card">
                {waadData.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <FaSearch className="text-muted" size={48} />
                        </div>
                        <h5>No Waad Found</h5>
                        <p className="text-muted">
                            {waadFilters.search || waadFilters.status
                                ? "Try adjusting your search or filter criteria"
                                : "Click 'Add Waad' to create your first waad"}
                        </p>
                        {(waadFilters.search || waadFilters.status) && (
                            <button className="btn btn-primary" onClick={clearFilters}>
                                Clear Filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="st-table-responsive">
                        <table className="table st-custom-table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Waad Name</th>
                                    <th>Waad Code</th>
                                    <th>Description</th>
                                    <th>Gaons</th>
                                    <th>Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {waadData.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>
                                            {(currentPage - 1) * (waadFilters.limit || 10) + index + 1}
                                        </td>
                                        <td className="fw-semibold">
                                            {item?.waad_name || 'N/A'}
                                        </td>
                                        <td>{item?.waad_code || 'N/A'}</td>
                                        <td>
                                            {item?.description ? (
                                                <span title={item.description}>
                                                    {item.description.length > 30 
                                                        ? item.description.substring(0, 30) + '...' 
                                                        : item.description}
                                                </span>
                                            ) : 'N/A'}
                                        </td>
                                        <td>
                                            <span className="badge bg-info">
                                                {item?.gaon_count || 0}
                                            </span>
                                        </td>
                                        <td>
                                            {/* ✅ Status Badge - 1 = Active, 0 = Inactive */}
                                            <span className={`badge ${isActive(item.status) ? 'bg-success' : 'bg-danger'}`}>
                                                {isActive(item.status) ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {/* VIEW */}
                                            <button
                                                className="st-action-btn st-view-btn me-1"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "view-waad",
                                                            title: "View Waad",
                                                            data: item,
                                                        })
                                                    )
                                                }
                                                title="View Details"
                                            >
                                                <FaEye />
                                            </button>

                                            {/* EDIT */}
                                            <button
                                                className="st-action-btn st-edit-btn me-1"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "edit-waad",
                                                            title: "Edit Waad",
                                                            data: item,
                                                        })
                                                    )
                                                }
                                                title="Edit Waad"
                                            >
                                                <FaEdit />
                                            </button>

                                            {/* DELETE */}
                                            <button
                                                className="st-action-btn st-delete-btn"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "delete-waad",
                                                            title: "Delete Waad",
                                                            data: {
                                                                id: item.id,
                                                                name: item.waad_name,
                                                                apiUrl: "/waad-delete",
                                                                message: `Are you sure you want to delete "${item.waad_name}"? This will also delete all associated gaons.`,
                                                                onSuccess: refetchData,
                                                            },
                                                        })
                                                    )
                                                }
                                                title="Delete Waad"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}
                {showPagination && waadData.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
                        <div className="text-muted small mb-2 mb-md-0">
                            Showing {((currentPage - 1) * (waadFilters.limit || 10)) + 1} to{' '}
                            {Math.min(currentPage * (waadFilters.limit || 10), totalItems)} of {totalItems} entries
                        </div>
                        <Pagination
                            page={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default Waad;